import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getUserCredits,
  deductCredits as apiDeductCredits,
  addCredits as apiAddCredits,
  getUserProfile,
  getOrCreateD1Chart,
  getOrCreateD9Chart
} from './api';

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ CONSTANTS - REUSABLE ACROSS THE APPLICATION
// ═══════════════════════════════════════════════════════════════════════════════

const STORAGE_KEYS = {
  USER: 'astroguru_user',
  TOKEN: 'astroguru_token',
  D1_CHART: (userId) => `astroguru_d1chart_${userId}`,
  D9_CHART: (userId) => `astroguru_d9chart_${userId}`,
  CHAT: (userId) => `astroguru_chat_${userId}`,
  FIRST_QUESTION: (userId) => `astroguru_first_question_${userId}`,
  DRAFT: (userId) => `astroguru_draft_${userId}`,
  SHORT_RESPONSE: (userId) => `astroguru_short_response_${userId}`
};

const TIMEZONE_MAP = {
  'AEST': 'Australia/Sydney',
  'AEDT': 'Australia/Sydney',
  'AWST': 'Australia/Perth',
  'ACST': 'Australia/Adelaide',
  'ACDT': 'Australia/Adelaide',
  'IST': 'Asia/Kolkata',
  'PST': 'America/Los_Angeles',
  'EST': 'America/New_York',
  'CST': 'America/Chicago',
  'MST': 'America/Denver',
  'GMT': 'Europe/London',
  'UTC': 'UTC'
};

const RELIGION_GREETINGS = {
  'Islam': 'Assalamu Alaikum',
  'Christianity': 'May God bless you',
  'Sikhism': 'Sat Sri Akal',
  'Buddhism': 'May Buddha\'s wisdom guide you',
  'Jainism': 'Jai Jinendra',
  'Judaism': 'Shalom',
  'Zoroastrianism': 'Asha Vahishta',
  'Bahai': 'Allah-u-Abha',
  'Hindu': 'Namaste',
  'Other': 'Divine blessings'
};

const RELIGION_BLESSINGS = {
  'Islam': 'Allah\'s blessings',
  'Christianity': 'God\'s grace',
  'Sikhism': 'Waheguru\'s blessings',
  'Buddhism': 'Buddha\'s wisdom',
  'Jainism': 'Tirthankara\'s guidance',
  'Judaism': 'Hashem\'s blessings',
  'Zoroastrianism': 'Ahura Mazda\'s light',
  'Bahai': 'Bahá\'u\'lláh\'s guidance',
  'Hindu': 'Divine blessings',
  'Other': 'Universal blessings'
};

const CREDIT_COSTS = {
  question: { first: 10, regular: 5 },
  detailed_reading: 15,
  compatibility: 8,
  default: 5
};

const DEFAULT_VALUES = {
  language: 'ENGLISH',
  credits: 20,
  creditsLimit: 0,
  timezone: 'Asia/Kolkata',
  place: 'Unknown',
  religion: 'Hindu',
  latitude: 0,
  longitude: 0
};

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ CONTEXT SETUP
// ═══════════════════════════════════════════════════════════════════════════════

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ HELPER FUNCTIONS - EXTRACTED FOR REUSABILITY
// ═══════════════════════════════════════════════════════════════════════════════

const formatBirthDate = (birthDate) => {
  if (birthDate instanceof Date) {
    return birthDate.toISOString().split('T')[0];
  }
  if (typeof birthDate === 'string' && birthDate.includes('T')) {
    return birthDate.split('T')[0];
  }
  return birthDate;
};

const formatBirthTime = (birthTime) => {
  if (birthTime instanceof Date) {
    const hours = String(birthTime.getUTCHours()).padStart(2, '0');
    const minutes = String(birthTime.getUTCMinutes()).padStart(2, '0');
    const seconds = String(birthTime.getUTCSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }
  if (typeof birthTime === 'string' && birthTime.includes('T')) {
    return birthTime.split('T')[1].split('.')[0];
  }
  return birthTime;
};

const normalizeTimezone = (timezone) => {
  const normalized = TIMEZONE_MAP[timezone] || timezone;
  
  if (normalized.length <= 4) {
    console.warn(`⚠️ Unknown timezone abbreviation: ${timezone}, defaulting to ${DEFAULT_VALUES.timezone}`);
    return DEFAULT_VALUES.timezone;
  }
  
  return normalized;
};

const buildChartData = (user) => {
  const birthDate = formatBirthDate(user.birth_date);
  const birthTime = formatBirthTime(user.birth_time);
  
  if (!birthTime || !birthTime.includes(':')) {
    throw new Error(`Invalid birth_time format: ${birthTime}`);
  }

  const datetime = `${birthDate}T${birthTime}`;
  const timezone = normalizeTimezone(user.timezone || DEFAULT_VALUES.timezone);

  return {
    user_id: user.userId,
    profile_id: null,
    name: user.full_name,
    datetime,
    latitude: user.latitude || DEFAULT_VALUES.latitude,
    longitude: user.longitude || DEFAULT_VALUES.longitude,
    timezone,
    place: user.birth_place || DEFAULT_VALUES.place,
    religion: user.religion || DEFAULT_VALUES.religion
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ AUTH PROVIDER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(null);
  const [d1ChartData, setD1ChartData] = useState(null);
  const [d1ChartLoading, setD1ChartLoading] = useState(false);
  const [d9ChartData, setD9ChartData] = useState(null);
  const [d9ChartLoading, setD9ChartLoading] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ LOG USER STATE CHANGES
  // ═══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    console.log('\n👤 ========== USER STATE CHANGED ==========');
    console.log('👤 User State:', {
      userId: user?.userId,
      fullName: user?.full_name,
      email: user?.email,
      credits: user?.credits,
      creditsLimit: user?.credits_limit,
      hasBirthDate: !!user?.birth_date,
      hasBirthTime: !!user?.birth_time,
      birthPlace: user?.birth_place,
      timezone: user?.timezone,
      religion: user?.religion,
      language: user?.preferred_language,
      hasD1Chart: !!d1ChartData,
      hasD9Chart: !!d9ChartData
    });
    console.log('👤 ==========================================\n');
  }, [user, d1ChartData, d9ChartData]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ LOG D1 CHART STATE CHANGES
  // ═══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (d1ChartData) {
      console.log('\n📊 ========== D1 CHART STATE UPDATED ==========');
      console.log('📊 D1 Chart Data:', {
        chartId: d1ChartData.d1ChartsId,
        source: d1ChartData.source,
        ayanamsa: d1ChartData.ayanamsa,
        sunSign: d1ChartData.sun_sign,
        moonSign: d1ChartData.moon_sign,
        grahaTableExists: !!d1ChartData.graha_table,
        grahaTableType: typeof d1ChartData.graha_table,
        grahaCount: Array.isArray(d1ChartData.graha_table) ? d1ChartData.graha_table.length : 0,
        loadedAt: d1ChartData.loadedAt,
        dataKeys: Object.keys(d1ChartData)
      });
      console.log('📊 ==========================================\n');
    }
  }, [d1ChartData]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ LOG D9 CHART STATE CHANGES
  // ═══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (d9ChartData) {
      console.log('\n📊 ========== D9 CHART STATE UPDATED ==========');
      console.log('📊 D9 Chart Data:', {
        chartId: d9ChartData.d9ChartsId,
        source: d9ChartData.source,
        ayanamsa: d9ChartData.ayanamsa,
        chartType: d9ChartData.chart_type,
        grahaTableExists: !!d9ChartData.graha_table,
        grahaTableType: typeof d9ChartData.graha_table,
        grahaCount: Array.isArray(d9ChartData.graha_table) ? d9ChartData.graha_table.length : 0,
        loadedAt: d9ChartData.loadedAt,
        dataKeys: Object.keys(d9ChartData)
      });
      console.log('📊 ==========================================\n');
    }
  }, [d9ChartData]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ LOAD USER AND TOKEN ON MOUNT
  // ═══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const loadStoredUser = async () => {
      console.log('\n🔄 ========== LOADING STORED USER ==========');
      
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
      const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);

      console.log('🔍 LocalStorage Check:', {
        hasStoredUser: !!storedUser,
        hasStoredToken: !!storedToken,
        userDataLength: storedUser?.length,
        tokenLength: storedToken?.length
      });

      if (storedUser && storedToken) {
        try {
          const userData = JSON.parse(storedUser);
          console.log('✅ Parsed User Data:', {
            userId: userData.userId,
            email: userData.email,
            fullName: userData.full_name,
            credits: userData.credits,
            hasBirthDate: !!userData.birth_date,
            hasBirthTime: !!userData.birth_time,
            birthPlace: userData.birth_place,
            timezone: userData.timezone,
            religion: userData.religion
          });

          setTokenState(storedToken);

          // Sync credits from backend if userId exists
          if (userData.userId) {
            try {
              console.log('🔄 Syncing user data from backend...');
              const creditsResponse = await getUserCredits(userData.userId);
              if (creditsResponse?.data?.success) {
                userData.credits = creditsResponse.data.balance.currentCredits;
                userData.credits_limit = creditsResponse.data.balance.creditsLimit;
                console.log('✅ Synced credits from database:', {
                  current: userData.credits,
                  limit: userData.credits_limit
                });
              }
            } catch (e) {
              console.error(' Failed to sync user data from backend:', e);
            }
          }

          setUserState(userData);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
          
          console.log('✅ User loaded successfully');
        } catch (error) {
          console.error(' Error parsing stored user:', error);
          localStorage.removeItem(STORAGE_KEYS.USER);
          localStorage.removeItem(STORAGE_KEYS.TOKEN);
        }
      } else {
        console.log('ℹ️ No stored user or token found');
      }

      console.log('🔄 ==========================================\n');
      setLoading(false);
    };

    loadStoredUser();
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ LOAD D1 AND D9 CHARTS FROM LOCALSTORAGE ON USER CHANGE
  // ═══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!user?.userId) {
      console.log('⏸️ No userId, skipping chart load from localStorage');
      return;
    }

    console.log('\n💾 ========== LOADING CHARTS FROM LOCALSTORAGE ==========');
    console.log('💾 Loading charts for userId:', user.userId);

    // Load D1 Chart
    const d1StorageKey = STORAGE_KEYS.D1_CHART(user.userId);
    const storedD1Chart = localStorage.getItem(d1StorageKey);
    
    console.log('📊 D1 Chart Storage Check:', {
      key: d1StorageKey,
      exists: !!storedD1Chart,
      dataLength: storedD1Chart?.length
    });

    if (storedD1Chart) {
      try {
        const chartData = JSON.parse(storedD1Chart);
        setD1ChartData(chartData);
        console.log('🔄 Loaded D1 chart from localStorage:', {
          chartId: chartData.d1ChartsId,
          source: chartData.source,
          grahaCount: chartData.graha_table?.length || 0,
          hasGrahaTable: !!chartData.graha_table
        });
      } catch (error) {
        console.error(' Error parsing stored D1 chart:', error);
        localStorage.removeItem(d1StorageKey);
      }
    } else {
      console.log('ℹ️ No D1 chart found in localStorage');
    }

    // Load D9 Chart
    const d9StorageKey = STORAGE_KEYS.D9_CHART(user.userId);
    const storedD9Chart = localStorage.getItem(d9StorageKey);
    
    console.log('📊 D9 Chart Storage Check:', {
      key: d9StorageKey,
      exists: !!storedD9Chart,
      dataLength: storedD9Chart?.length
    });

    if (storedD9Chart) {
      try {
        const chartData = JSON.parse(storedD9Chart);
        setD9ChartData(chartData);
        console.log('✅ Loaded D9 chart from localStorage:', {
          chartId: chartData.d9ChartsId,
          source: chartData.source,
          grahaCount: chartData.graha_table?.length || 0,
          hasGrahaTable: !!chartData.graha_table
        });
      } catch (error) {
        console.error(' Error parsing stored D9 chart:', error);
        localStorage.removeItem(d9StorageKey);
      }
    } else {
      console.log('ℹ️ No D9 chart found in localStorage');
    }

    console.log('💾 ==========================================\n');
  }, [user?.userId]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ LOGIN FUNCTION
  // ═══════════════════════════════════════════════════════════════════════════════

  const login = async (userData, authToken) => {
    console.log('\n🔐 ========== LOGIN INITIATED ==========');
    console.log('🔐 Login Data:', {
      userId: userData.userId,
      email: userData.email,
      fullName: userData.full_name,
      hasToken: !!authToken,
      tokenLength: authToken?.length
    });

    let preferredLanguage = userData.preferred_language || DEFAULT_VALUES.language;
    let currentCredits = userData.credits || DEFAULT_VALUES.credits;
    let creditsLimit = userData.credits_limit || DEFAULT_VALUES.creditsLimit;

    // Sync data from backend if userId exists
    if (userData.userId) {
      try {
        console.log('📊 Fetching user data for userId:', userData.userId);
        const creditsResponse = await getUserCredits(userData.userId);
        if (creditsResponse?.data?.success) {
          currentCredits = creditsResponse.data.balance.currentCredits;
          creditsLimit = creditsResponse.data.balance.creditsLimit;
          console.log('✅ Loaded credits from database:', {
            current: currentCredits,
            limit: creditsLimit
          });
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch user data from database, using login data:', err.message);
      }
    } else {
      console.warn('⚠️ No userId found in userData - skipping database sync');
    }

    const completeUserData = {
      ...userData,
      preferred_language: preferredLanguage,
      credits: currentCredits,
      credits_limit: creditsLimit
    };

    console.log('💾 Saving complete user data:', {
      userId: completeUserData.userId,
      credits: completeUserData.credits,
      language: completeUserData.preferred_language,
      hasBirthDate: !!completeUserData.birth_date,
      hasBirthTime: !!completeUserData.birth_time
    });

    setTokenState(authToken);
    setUserState(completeUserData);
    localStorage.setItem(STORAGE_KEYS.TOKEN, authToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(completeUserData));

    console.log('✅ User and token saved successfully');
    console.log('🔐 ==========================================\n');
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ SAFE SET USER FUNCTION (PRESERVES TOKEN)
  // ═══════════════════════════════════════════════════════════════════════════════

  const setUser = (userData) => {
    console.log('\n🔄 ========== SET USER CALLED ==========');
    console.log('🔄 Update Data:', {
      userId: userData?.userId,
      credits: userData?.credits,
      hasAllFields: !!userData?.email,
      keysProvided: userData ? Object.keys(userData) : []
    });

    const currentToken = token || localStorage.getItem(STORAGE_KEYS.TOKEN);

    if (!currentToken) {
      console.warn('⚠️ No token found when updating user - this might cause auth issues');
    }

    const existingUser = user || JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || '{}');
    
    const updatedUserData = {
      ...existingUser,
      ...userData
    };

    console.log('💾 Updated User Data:', {
      userId: updatedUserData.userId,
      credits: updatedUserData.credits,
      tokenStillExists: !!currentToken,
      hasBirthDate: !!updatedUserData.birth_date,
      hasBirthTime: !!updatedUserData.birth_time
    });

    setUserState(updatedUserData);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUserData));

    console.log('✅ User updated successfully (token preserved)');
    console.log('🔄 ==========================================\n');
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ LOGOUT FUNCTION
  // ═══════════════════════════════════════════════════════════════════════════════

  const logout = () => {
    console.log('\n👋 ========== LOGOUT INITIATED ==========');
    console.log('👋 Logging out user:', user?.email);

    setUserState(null);
    setTokenState(null);
    setD1ChartData(null);
    setD9ChartData(null);

    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);

    if (user?.userId) {
      const keysToRemove = [
        STORAGE_KEYS.CHAT(user.userId),
        STORAGE_KEYS.FIRST_QUESTION(user.userId),
        STORAGE_KEYS.DRAFT(user.userId),
        STORAGE_KEYS.SHORT_RESPONSE(user.userId),
        STORAGE_KEYS.D1_CHART(user.userId),
        STORAGE_KEYS.D9_CHART(user.userId)
      ];

      console.log('🗑️ Removing user-specific data:', keysToRemove);
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }

    console.log('✅ User and token cleared');
    console.log('👋 ==========================================\n');
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ GENERIC CHART LOADING FUNCTION (DRY PRINCIPLE)
  // ═══════════════════════════════════════════════════════════════════════════════

  const loadChart = async (chartType, forceRefresh = false) => {
    const isD1 = chartType === 'D1';
    const chartData = isD1 ? d1ChartData : d9ChartData;
    const setChartData = isD1 ? setD1ChartData : setD9ChartData;
    const setChartLoading = isD1 ? setD1ChartLoading : setD9ChartLoading;
    const storageKey = isD1 ? STORAGE_KEYS.D1_CHART : STORAGE_KEYS.D9_CHART;
    const apiFunction = isD1 ? getOrCreateD1Chart : getOrCreateD9Chart;
    const chartIdKey = isD1 ? 'd1ChartsId' : 'd9ChartsId';

    console.log(`\n📊 ========== LOAD ${chartType} CHART START ==========`);
    console.log(`📊 Load Options:`, {
      forceRefresh,
      hasExistingData: !!chartData,
      userId: user?.userId,
      hasBirthDate: !!user?.birth_date,
      hasBirthTime: !!user?.birth_time
    });
    
    if (!user?.userId) {
      console.log(`⚠️ Cannot load ${chartType} chart: No user ID`);
      console.log(`📊 ========== LOAD ${chartType} CHART END (NO USER) ==========\n`);
      return null;
    }

    if (chartData && !forceRefresh) {
      console.log(`✅ ${chartType} chart already loaded, returning cached data`);
      console.log(`📊 ========== LOAD ${chartType} CHART END (CACHED) ==========\n`);
      return chartData;
    }

    if (!user?.birth_date || !user?.birth_time) {
      console.log(`⚠️ Cannot load ${chartType} chart: Missing birth data`, {
        hasBirthDate: !!user?.birth_date,
        hasBirthTime: !!user?.birth_time,
        birthDate: user?.birth_date,
        birthTime: user?.birth_time
      });
      console.log(`📊 ========== LOAD ${chartType} CHART END (NO BIRTH DATA) ==========\n`);
      return null;
    }

    try {
      setChartLoading(true);
      console.log(`🔄 Loading ${chartType} chart for user ${user.userId}...`);

      const chartRequestData = buildChartData(user);
      console.log(`📤 ${chartType} Chart Request:`, JSON.stringify(chartRequestData, null, 2));

      const response = await apiFunction(chartRequestData);

      if (response.data.success) {
        const chartInfo = {
          ...response.data.data,
          source: response.data.source,
          loadedAt: new Date().toISOString()
        };

        console.log(`✅ ${chartType} Chart Loaded:`, {
          chartId: chartInfo[chartIdKey],
          source: chartInfo.source,
          ayanamsa: chartInfo.ayanamsa,
          grahaCount: chartInfo.graha_table?.length || 0,
          hasGrahaTable: !!chartInfo.graha_table,
          grahaTableType: typeof chartInfo.graha_table,
          grahaTableIsArray: Array.isArray(chartInfo.graha_table),
          dataKeys: Object.keys(chartInfo)
        });

        // ✅ Set state
        setChartData(chartInfo);

        // ✅ Save to localStorage
        const storageKeyForUser = storageKey(user.userId);
        localStorage.setItem(storageKeyForUser, JSON.stringify(chartInfo));
        console.log(`💾 ${chartType} chart saved to localStorage:`, storageKeyForUser);

        console.log(`📊 ========== LOAD ${chartType} CHART END (SUCCESS) ==========\n`);
        return chartInfo;
      } else {
        console.error(` Failed to load ${chartType} chart:`, response.data.message);
        console.log(`📊 ========== LOAD ${chartType} CHART END (API FAILED) ==========\n`);
        return null;
      }
    } catch (error) {
      console.error(` Failed to load ${chartType} chart:`, error.message);
      if (error.response?.data) {
        console.error(`📋 ${chartType} Chart API Error Details:`, error.response.data);
      }
      console.log(`📊 ========== LOAD ${chartType} CHART END (ERROR) ==========\n`);
      return null;
    } finally {
      setChartLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ D1 CHART FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════════

  const loadD1Chart = (forceRefresh = false) => loadChart('D1', forceRefresh);

  const clearD1Chart = () => {
    console.log('🗑️ Clearing D1 chart data');
    setD1ChartData(null);
    if (user?.userId) {
      localStorage.removeItem(STORAGE_KEYS.D1_CHART(user.userId));
    }
  };

  const refreshD1Chart = () => {
    console.log('🔄 Refreshing D1 chart data');
    return loadD1Chart(true);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ D9 CHART FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════════

  const loadD9Chart = (forceRefresh = false) => loadChart('D9', forceRefresh);

  const clearD9Chart = () => {
    console.log('🗑️ Clearing D9 chart data');
    setD9ChartData(null);
    if (user?.userId) {
      localStorage.removeItem(STORAGE_KEYS.D9_CHART(user.userId));
    }
  };

  const refreshD9Chart = () => {
    console.log('🔄 Refreshing D9 chart data');
    return loadD9Chart(true);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ CREDIT FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════════

  const deductCredits = async (amount, reason = 'Question asked') => {
    if (!user?.userId) {
      console.error(' Cannot deduct credits: User not found');
      return {
        success: false,
        error: 'User not found',
        currentCredits: user?.credits || 0
      };
    }

    if (user.credits < amount) {
      console.log('💰 Insufficient local credits detected');
      return {
        success: false,
        error: 'Insufficient credits',
        currentCredits: user.credits,
        requiredCredits: amount
      };
    }

    try {
      console.log(`💸 Deducting ${amount} credits for userId ${user.userId}: ${reason}`);
      const response = await apiDeductCredits(user.userId, amount, reason);

      if (response.data.success) {
        setUser({
          ...user,
          credits: response.data.balance.currentCredits
        });

        console.log('✅ Credits deducted successfully:', {
          previous: response.data.balance.previousCredits,
          deducted: response.data.balance.deductedAmount,
          current: response.data.balance.currentCredits
        });

        return {
          success: true,
          previousCredits: response.data.balance.previousCredits,
          deductedAmount: response.data.balance.deductedAmount,
          currentCredits: response.data.balance.currentCredits,
          reason,
          message: response.data.message
        };
      } else {
        console.error(' Failed to deduct credits:', response.data.error);
        return {
          success: false,
          error: response.data.error,
          currentCredits: user.credits
        };
      }
    } catch (error) {
      console.error(' Credit deduction error:', error);

      if (error.response?.data?.error === 'Insufficient credits') {
        return {
          success: false,
          error: 'Insufficient credits',
          currentCredits: error.response.data.balance?.currentCredits || user.credits,
          requiredCredits: error.response.data.balance?.requiredCredits || amount
        };
      }

      return {
        success: false,
        error: 'Network error. Please try again.',
        currentCredits: user.credits
      };
    }
  };

  const addCredits = async (amount, reason = 'Credits purchased', packageInfo = null) => {
    if (!user?.userId) {
      console.error(' Cannot add credits: User not found');
      return {
        success: false,
        error: 'User not found'
      };
    }

    try {
      console.log(`💰 Adding ${amount} credits for userId ${user.userId}: ${reason}`);
      const response = await apiAddCredits(user.userId, amount, reason, packageInfo);

      if (response.data.success) {
        setUser({
          ...user,
          credits: response.data.balance.currentCredits
        });

        console.log('✅ Credits added successfully:', {
          previous: response.data.balance.previousCredits,
          added: response.data.balance.addedAmount,
          current: response.data.balance.currentCredits
        });

        return {
          success: true,
          previousCredits: response.data.balance.previousCredits,
          addedAmount: response.data.balance.addedAmount,
          currentCredits: response.data.balance.currentCredits,
          reason,
          packageInfo,
          message: response.data.message
        };
      } else {
        console.error(' Failed to add credits:', response.data.error);
        return {
          success: false,
          error: response.data.error
        };
      }
    } catch (error) {
      console.error(' Credit addition error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.'
      };
    }
  };

  const refreshCredits = async () => {
    if (!user?.userId) {
      console.log('⚠️ Cannot refresh credits: No user ID');
      return;
    }

    try {
      console.log('🔄 Refreshing credits for userId:', user.userId);
      const response = await getUserCredits(user.userId);

      if (response.data.success) {
        setUser({
          ...user,
          credits: response.data.balance.currentCredits,
          credits_limit: response.data.balance.creditsLimit
        });

        console.log('✅ Credits refreshed:', {
          current: response.data.balance.currentCredits,
          limit: response.data.balance.creditsLimit
        });

        return response.data.balance.currentCredits;
      }
    } catch (error) {
      console.error(' Failed to refresh credits:', error);
    }
  };

  const refreshUser = async () => {
    if (!user?.userId) {
      console.log('⚠️ Cannot refresh user: No user ID');
      return;
    }

    try {
      console.log('🔄 Refreshing user profile for userId:', user.userId);
      const response = await getUserProfile(user.userId);

      if (response.data.success) {
        setUser({
          ...user,
          ...response.data.user,
          userId: user.userId,
          id: user.id
        });
        
        console.log('✅ User profile refreshed');
        return user;
      }
    } catch (error) {
      console.error(' Failed to refresh user profile:', error);
    }
  };

  const hasEnoughCredits = (amount) => user?.credits >= amount;

  const getCreditCost = (action = 'question', isFirstQuestion = false) => {
    if (action === 'question') {
      return isFirstQuestion ? CREDIT_COSTS.question.first : CREDIT_COSTS.question.regular;
    }
    return CREDIT_COSTS[action] || CREDIT_COSTS.default;
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ RELIGION FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════════

  const getReligionGreeting = (religion) => RELIGION_GREETINGS[religion] || RELIGION_GREETINGS.Hindu;

  const getReligionBlessing = (religion) => RELIGION_BLESSINGS[religion] || RELIGION_BLESSINGS.Hindu;

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ CONTEXT VALUE
  // ═══════════════════════════════════════════════════════════════════════════════

  const value = {
    // User & Auth
    user,
    token,
    setUser,
    login,
    logout,
    loading,

    // Credits
    deductCredits,
    addCredits,
    refreshCredits,
    refreshUser,
    hasEnoughCredits,
    getCreditCost,

    // Religion
    getReligionGreeting,
    getReligionBlessing,

    // D1 Chart
    d1ChartData,
    d1ChartLoading,
    loadD1Chart,
    clearD1Chart,
    refreshD1Chart,

    // D9 Chart
    d9ChartData,
    d9ChartLoading,
    loadD9Chart,
    clearD9Chart,
    refreshD9Chart
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
